import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api, { tokenStore } from '@/lib/api';
import { formatDate } from '@/lib/dates';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { AdminBottomNav } from './AdminBottomNav';
import { User, LogOut, Sun, Moon, Palette, Settings, CreditCard, Rocket, ShieldAlert } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useScrolled } from '@/lib/useScrolled';
import { BILLING_HIDDEN_SOFT_LAUNCH } from '@/lib/softLaunchFlags';

const AVATAR_COLORS = [
  '#E91E8C', '#6C3CE0', '#FF6B35', '#00C2A8', '#4ECDC4',
  '#3498DB', '#9B59B6', '#E91E63', '#00BCD4', '#FF5722',
];

function getAvatarColor(name) {
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name) {
  if (!name) return 'A';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
}

const THEME_MODES = [
  // 'system' mode was removed Feb 2026 — it caused a flash-of-wrong-theme
  // on initial load. The user picks light/dark explicitly now.
  { id: 'light', icon: Sun, label: 'Light' },
  { id: 'dark', icon: Moon, label: 'Dark' },
];

function ThemeSwitcher({ mode, setTheme }) {
  return (
    <div className="px-3 py-2">
      <div className="flex items-center justify-evenly rounded-full bg-muted p-1" data-testid="admin-theme-switcher">
        {THEME_MODES.map(({ id, icon: Icon, label }) => (
          <button
            key={id}
            onClick={() => setTheme(id)}
            className={`flex items-center justify-center w-8 h-8 rounded-full transition-all ${
              mode === id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
            title={label}
            data-testid={`admin-theme-${id}`}
          >
            <Icon className="w-4 h-4" strokeWidth={1.8} />
          </button>
        ))}
      </div>
    </div>
  );
}

function MenuSeparator() {
  return <div role="separator" className="-mx-1 my-1 h-px bg-muted" />;
}

function MenuItem({ onClick, children, className = '', testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left flex select-none items-center gap-2 rounded-sm px-3 py-2 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground active:bg-accent ${className}`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

/**
 * Custom user-avatar dropdown that does NOT use Radix portals/popper.
 * Radix's getBoundingClientRect-based positioning is unreliable on iOS Safari
 * & Android Chrome when the trigger lives inside a position:sticky header
 * (returns y=-271px instead of the visible position). This menu is just a
 * position:absolute panel anchored to the trigger's wrapper, with a click-
 * outside listener and the same testid contract for E2E tests.
 */
function TopBarUserMenu({ user, mode, setTheme, logout, navigate }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold hover:opacity-90 transition-opacity ring-2 ring-background overflow-hidden"
        style={{ backgroundColor: user?.society_logo ? 'transparent' : getAvatarColor(user?.name) }}
        data-testid="top-bar-user-menu"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={user?.society_name ? `${user.society_name} menu` : 'Account menu'}
      >
        {user?.society_logo ? (
          <img
            src={user.society_logo}
            alt=""
            className="w-full h-full object-cover"
            data-testid="top-bar-user-menu-logo"
          />
        ) : (
          getInitials(user?.name)
        )}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-56 rounded-md border bg-popover text-popover-foreground shadow-lg z-50 p-1 max-h-[calc(100vh-120px)] overflow-y-auto"
          data-testid="top-bar-user-menu-panel"
        >
          <div className="px-3 py-2">
            <p className="text-sm font-medium truncate">{user?.name || 'Admin'}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
          <MenuSeparator />
          <MenuItem onClick={() => go('/admin/profile')} testId="top-bar-profile-link">
            <User className="w-3.5 h-3.5 mr-2" /> My Profile
          </MenuItem>
          <MenuItem onClick={() => go('/admin/branding')} testId="top-bar-branding-link">
            <Palette className="w-3.5 h-3.5 mr-2" /> Branding
          </MenuItem>
          <MenuItem onClick={() => go('/admin/settings')} testId="top-bar-settings-link">
            <Settings className="w-3.5 h-3.5 mr-2" /> Settings
          </MenuItem>
          {!BILLING_HIDDEN_SOFT_LAUNCH && (
            <MenuItem onClick={() => go('/admin/billing')} testId="top-bar-billing-link">
              <CreditCard className="w-3.5 h-3.5 mr-2" /> Billing
            </MenuItem>
          )}
          <MenuSeparator />
          <ThemeSwitcher mode={mode} setTheme={setTheme} />
          <MenuSeparator />
          <MenuItem
            onClick={() => { setOpen(false); logout(); navigate('/', { replace: true }); }}
            className="text-destructive"
            testId="top-bar-logout"
          >
            <LogOut className="w-3.5 h-3.5 mr-2" /> Log out
          </MenuItem>
        </div>
      )}
    </div>
  );
}

export const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const { mode, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const scrolled = useScrolled();
  const firstName = user?.name?.split(' ')[0] || 'Admin';
  const scrollRef = useRef(null);

  const impersonatedBy = user?.impersonated_by;
  const exitImpersonation = async () => {
    try { await api.post('/auth/logout'); } catch (err) { /* cookie clear is best-effort */ }
    tokenStore.clearAdmin();
    api.clearCache?.();
    // cms_token session is untouched, so this lands back in the CMS.
    window.location.href = '/cms/clients';
  };

  // Reset the inner scroll container to the top on every route change.
  // Without this, iOS Safari's address-bar collapse during navigation can
  // leave the new page mounted at a non-zero scrollTop, making it look as
  // though the page loaded "scrolled down a small bit by default".
  // We run the reset in two animation frames: the first runs synchronously
  // after layout, the second runs after iOS has finished its address-bar
  // animation (which can shift the page after the initial paint).
  useEffect(() => {
    const reset = () => {
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0);
        // iOS Safari sometimes scrolls the documentElement directly when a
        // form input has been focused — window.scrollTo alone isn't enough.
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
      }
    };
    reset();
    const r1 = requestAnimationFrame(() => {
      reset();
      const r2 = requestAnimationFrame(reset);
      return () => cancelAnimationFrame(r2);
    });
    return () => cancelAnimationFrame(r1);
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen bg-background app-bg-grey" data-testid="app-shell">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-[72px] flex-shrink-0 sticky top-0 h-screen">
        <Sidebar />
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {impersonatedBy && (
          <div
            className="sticky top-0 z-50 flex items-center justify-between gap-3 px-4 sm:px-6 lg:px-8 py-2 bg-amber-500 text-white text-xs sm:text-sm font-medium shadow-sm"
            data-testid="impersonation-banner"
          >
            <span className="flex items-center gap-2 min-w-0">
              <ShieldAlert className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">
                Support mode — viewing <strong>{user?.society_name || 'this client'}</strong> as {user?.email}
              </span>
            </span>
            <button
              type="button"
              onClick={exitImpersonation}
              className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full bg-white/20 hover:bg-white/30 px-3 py-1 font-semibold transition-colors"
              data-testid="exit-impersonation-btn"
            >
              <LogOut className="w-3.5 h-3.5" /> Exit
            </button>
          </div>
        )}
        {/* Top bar — sticky; border only appears once content has scrolled under it */}
        <div className={`sticky ${impersonatedBy ? 'top-9' : 'top-0'} z-40 flex items-center justify-between px-4 sm:px-6 lg:px-8 py-3 flex-shrink-0 transition-[background-color,backdrop-filter,border-color] duration-200 ${scrolled ? 'bg-background/85 backdrop-blur-md border-b border-border' : 'bg-transparent border-b border-transparent'}`}>
          <div className="flex items-center gap-3">
            <img src="/brand/icon-logo.png" alt="SportSweeps" className="w-8 h-8 rounded-lg object-contain md:hidden" data-testid="mobile-logo" />
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-foreground"
                data-testid="top-bar-greeting">Hey, {firstName}</h1>
              <p className="text-[10px] text-muted-foreground leading-tight">
                {user?.society_name} &middot; {formatDate(new Date().toISOString())}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Desktop-only quick action: jump straight into pool launch
                from anywhere in the admin app. Mobile already exposes
                this CTA inline on the dashboard so we hide it here on
                <md to avoid eating up cramped header real estate. */}
            <button
              type="button"
              onClick={() => navigate('/admin/tournaments?from=launch')}
              className="hidden md:inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-xs font-semibold text-white bg-[#6C3CE0] hover:bg-[#5a30c4] active:bg-[#4f29b0] transition-colors shadow-sm"
              data-testid="top-bar-launch-pool"
              aria-label="Launch a pool"
            >
              <Rocket className="w-3.5 h-3.5" strokeWidth={2.2} />
              <span>Launch Pool</span>
            </button>
            <TopBarUserMenu user={user} mode={mode} setTheme={setTheme} logout={logout} navigate={navigate} />
          </div>
        </div>

        {/* Page content — bottom padding for mobile nav */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto pb-20 md:pb-6">
          <div className="px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-6">
            {children}
          </div>
        </div>
      </div>

      {/* Mobile bottom nav */}
      <div className="md:hidden">
        <AdminBottomNav />
      </div>
    </div>
  );
};
